import { useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useToast } from '@/components/ui/toast';
import {
  BeforeInstallPromptEvent,
  canUseNotifications,
  getOrCreatePwaDeviceId,
  sendSupabasePushNotification,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from '@/lib/pwa';

export function PwaPromptBar() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isEnablingNotifications, setIsEnablingNotifications] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof window !== 'undefined' && canUseNotifications() ? Notification.permission : 'unsupported'
  );

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
      addToast({
        title: 'App installed',
        description: 'Karebe is now available from your device like a native app.',
        variant: 'success',
      });
    };

    setIsInstalled(window.matchMedia('(display-mode: standalone)').matches);
    setNotificationPermission(canUseNotifications() ? Notification.permission : 'unsupported');
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [addToast]);

  const hasInstallPrompt = Boolean(installPromptEvent) && !isInstalled;
  const canAskNotifications = notificationPermission !== 'granted' && notificationPermission !== 'unsupported';

  const bannerVisible = useMemo(
    () => hasInstallPrompt || canAskNotifications || notificationPermission === 'granted',
    [canAskNotifications, hasInstallPrompt, notificationPermission]
  );
  const isAdminViewer = user?.role === 'admin' || user?.role === 'super-admin';

  const handleInstall = async () => {
    if (!installPromptEvent) return;

    await installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;

    if (choice.outcome === 'accepted') {
      addToast({
        title: 'Install started',
        description: 'Chrome is installing Karebe on this device.',
        variant: 'success',
      });
      setInstallPromptEvent(null);
      return;
    }

    addToast({
      title: 'Install dismissed',
      description: 'You can install the app later from the browser menu.',
      variant: 'info',
    });
  };

  const handleEnableNotifications = async () => {
    setIsEnablingNotifications(true);
    try {
      const result = await subscribeToPushNotifications({
        userId: user?.id || null,
        installed: isInstalled,
      });
      setNotificationPermission(result.permission);

      if (result.permission === 'granted') {
        addToast({
          title: 'Notifications enabled',
          description: 'This device is now registered for Karebe push notifications.',
          variant: 'success',
        });

        await sendSupabasePushNotification({
          action: 'send_test_notification',
          deviceId: result.deviceId,
          userId: user?.id || null,
          title: 'Notifications enabled',
          body: 'Karebe will send order updates to this installed app.',
          url: '/catalog',
          tag: 'notifications-enabled',
        });
        return;
      }

      addToast({
        title: result.permission === 'unsupported' ? 'Notifications unavailable' : 'Notifications blocked',
        description:
          result.permission === 'unsupported'
            ? 'This browser does not support push notifications.'
            : 'Allow notifications in your browser settings to enable them later.',
        variant: result.permission === 'unsupported' ? 'warning' : 'info',
      });
    } catch (error) {
      addToast({
        title: 'Notification setup failed',
        description: error instanceof Error ? error.message : 'Unable to enable notifications.',
        variant: 'error',
      });
    } finally {
      setIsEnablingNotifications(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await sendSupabasePushNotification({
        action: 'send_test_notification',
        deviceId: getOrCreatePwaDeviceId(),
        userId: user?.id || null,
        title: 'Order update test',
        body: 'This is how Karebe PWA push notifications will appear on your device.',
        url: '/catalog',
        tag: 'karebe-test',
      });
      addToast({
        title: 'Notification sent',
        description: 'Check your device notification tray.',
        variant: 'success',
      });
    } catch (error) {
      addToast({
        title: 'Notification failed',
        description: error instanceof Error ? error.message : 'Unable to send notification.',
        variant: 'error',
      });
    }
  };

  const handleDisableNotifications = async () => {
    try {
      await unsubscribeFromPushNotifications();
      setNotificationPermission(canUseNotifications() ? Notification.permission : 'unsupported');
      addToast({
        title: 'Notifications disabled',
        description: 'This device has been unsubscribed from Karebe push notifications.',
        variant: 'info',
      });
    } catch (error) {
      addToast({
        title: 'Unable to disable notifications',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'error',
      });
    }
  };

  if (!bannerVisible) {
    return null;
  }

  return (
    <div className="border-b border-brand-100 bg-gradient-to-r from-brand-50 via-white to-brand-50">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-brand-900">
            <Smartphone className="h-4 w-4 text-brand-600" />
            {isAdminViewer
              ? 'Install Karebe Admin and receive new-order alerts'
              : 'Install Karebe and turn on device notifications'}
          </p>
          <p className="mt-1 text-sm text-brand-600">
            {isAdminViewer
              ? 'Use the admin app in Chrome and get notified as soon as new orders are placed.'
              : 'Add the app to Chrome for a home-screen experience and order alerts.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {hasInstallPrompt && (
            <Button variant="outline" size="sm" onClick={handleInstall} className="rounded-full">
              <Download className="h-4 w-4" />
              Install app
            </Button>
          )}

          {canAskNotifications && (
            <Button size="sm" onClick={handleEnableNotifications} className="rounded-full" isLoading={isEnablingNotifications}>
              <Bell className="h-4 w-4" />
              Enable notifications
            </Button>
          )}

          {notificationPermission === 'granted' && (
            <>
              <Button variant="secondary" size="sm" onClick={handleTestNotification} className="rounded-full">
                <Bell className="h-4 w-4" />
                Test notification
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDisableNotifications} className="rounded-full">
                <BellOff className="h-4 w-4" />
                Disable
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PwaPromptBar;
